import React, { useState } from "react";
import { ImageUploadWrapper } from "~/components/ImageUploadWrapper";
import TextFormField from "~/components/text-form-field";
import DatePickFormField from "~/visuals/organisms/DatePickFormField";
import PageTitleRow from "~/components/page-title-row";
import FormCard from "~/components/form-card";
import {
  EeCodeRecord,
  Project,
  ThirdPartyInvoice,
  ValidationResponse,
} from "~/gql/types";
import ProjectPickerFormField from "~/visuals/organisms/ProjectPickerFormField";
import FormBottomRow from "~/components/form-bottom-row";
import { ErrorRenderer, MutationForm } from "~/components/forms/MutationForm";
import { useNavigate } from "react-router-dom";
import {
  thirdPartyInvoice as thirdPartyInvoiceRoute,
  thirdPartyInvoices,
} from "~/routes";
import "./ThirdPartyInvoiceForm.scss";
import moment, { Moment } from "moment";

import { Breadcrumb, useBreadcrumbs } from "~/main-layout/BreadcrumbProvider";
import { EeCodePickerFormField } from "~/visuals/organisms/EeCodePickerFormField";
import { useEeCodes } from "~/refdata2/eeCodes";
export type FormValues = {
  date: Moment;
  amount: string;
  project: Project;
  documentImage: Blob | null;
  documentImageId: string | null;
  eeCode: EeCodeRecord | null;
  notes: string | null;
};

type ThirdPartyInvoiceFormProps = {
  type: "Create" | "Edit";
  setImageId: React.Dispatch<string | null>;
  onSubmit: (value: any) => Promise<ValidationResponse>;
  onSuccess: (value: any) => void;
  thirdPartyInvoice?: ThirdPartyInvoice;
  initialValues?: FormValues;
  image?: any;
};

export const ThirdPartyInvoiceForm: React.FC<ThirdPartyInvoiceFormProps> = ({
  type,
  setImageId,
  thirdPartyInvoice,
  onSubmit,
  onSuccess,
  initialValues: initialFormValues,
  image,
}) => {
  const navigate = useNavigate();

  useBreadcrumbs(
    [
      { text: "Third Party Invoices", to: thirdPartyInvoices.path },
      type === "Create"
        ? { text: "Create " }
        : {
            text: thirdPartyInvoice?.invoiceNumber,
            to: thirdPartyInvoiceRoute.toRoute(thirdPartyInvoice!.invoiceNumber)
              .path,
          },
      type === "Edit" ? { text: "Edit" } : null,
    ].filter((x) => !!x) as Breadcrumb[],
    [thirdPartyInvoice, type]
  );

  const [imageUrl, setImageUrl] = useState<string | null>(
    thirdPartyInvoice?.documentUrl ?? null
  );

  const eeCodes = useEeCodes();

  const initialEeCode = thirdPartyInvoice
    ? eeCodes?.find((x) => x.eeCode === thirdPartyInvoice?.eeCode) ?? null
    : null;

  const initialValues = initialFormValues ?? {
    date: thirdPartyInvoice?.date
      ? moment(thirdPartyInvoice?.date, "YYYY-MM-DD")
      : null,
    amount: thirdPartyInvoice?.amount ?? "",
    project: thirdPartyInvoice?.project ?? null,
    documentImage: image && imageUrl ? image : null,
    eeCode: initialEeCode,
    notes: thirdPartyInvoice?.notes ?? null,
  };

  return (
    <>
      <div className="third-party-title">
        <PageTitleRow
          title={`${
            type === "Create"
              ? "Create Third Party Invoice"
              : `Edit ${thirdPartyInvoice?.invoiceNumber}`
          }`}
        />
      </div>
      <FormCard>
        <MutationForm
          {...{
            initialValues,
            onSuccess,
            runMutation: onSubmit,
            allowPristineSubmit: true,
          }}
        >
          <DatePickFormField
            formField={"date"}
            label={"Date"}
            helperText={""}
            required
          />
          <ProjectPickerFormField
            formField="project"
            label="Project"
            helperText=""
            required
            date=""
            filter={(x) => x.allowThirdPartyInvoice}
          />
          <EeCodePickerFormField
            formField={"eeCode"}
            label={"EE Code"}
            helperText={""}
            required
          />
          <TextFormField
            label="Amount"
            helperText=""
            formField="amount"
            type="number"
            required
          />
          <TextFormField label="Notes" helperText="" formField="notes" />
          <ImageUploadWrapper
            {...{
              setImageId,
              label: "document",
              formField: "documentImage",
              imageUrl,
              setImageUrl,
              required: true,
            }}
          />
          <ErrorRenderer
            render={(errorMessage) => (
              <FormBottomRow
                {...{
                  errorMessage,
                  buttonText: type == "Create" ? "Create" : "Save",
                  onCancel: () =>
                    navigate(
                      type == "Create"
                        ? thirdPartyInvoices.path
                        : thirdPartyInvoiceRoute.toRoute(
                            thirdPartyInvoice!.invoiceNumber
                          ).path
                    ),
                }}
              ></FormBottomRow>
            )}
          />
        </MutationForm>
      </FormCard>
    </>
  );
};
